import { get, post } from '@/services/http';

// Interfaces
interface ValidateCommandParams {
  command: string;
  measure: string;
  dimension?: string;
}

interface ValidateCommandResponse {
  valid: boolean;
  command_count: number;
  issues?: Array<{
    command: string;
    warning?: string;
    error?: string;
  }>;
  error?: string;
}

interface ProcessCommandParams {
  command: string;
  measure: string;
  dimension?: string;
  current_state?: {
    filters: any[];
    sort: any;
    columns?: any[];
  };
}

interface ProcessCommandResponse {
  status: 'success' | 'error';
  actions: Array<{
    type: 'filter' | 'sort' | 'columns';
    data: any;
  }>;
  summary?: string;
  error?: string;
  metadata?: {
    processed_at: string;
    measure: string;
    dimension?: string;
    command_count: number;
  };
}

interface GetCommandsParams {
  measure: string;
}

interface GetCommandsResponse {
  commands: Array<{
    command: string;
    pattern: string;
    description: string;
    examples: string[];
  }>;
  usage?: string;
  examples?: string[];
  merchant_examples?: string[];
  context?: {
    measure: string;
    note: string;
  };
}

interface BatchProcessParams {
  commands: string[];
  measure: string;
  dimension?: string;
  current_state?: {
    filters: any[];
    sort: any;
    columns?: any[];
  };
}

interface FilterAction {
  name: string;
  code: string;
  dataType: string;
  operator: string;
  value: any;
}

interface SortAction {
  column: string;
  columnName?: string;
  direction: 'asc' | 'desc';
}

interface ColumnsAction {
  operation: 'add' | 'remove' | 'only';
  columns: Array<{
    name: string;
    code: string;
    dataType?: string;
  }>;
}

const reportAssistantService = {
  /**
   * Validate a command before execution
   * @deprecated No longer needed - AI handles validation during processing
   * @param params - Command validation parameters
   * @returns Promise with validation result
   */
  async validateCommand(params: ValidateCommandParams): Promise<ValidateCommandResponse> {
    try {
      const response = await post('/report-config/validate', params) as any;
      return response.data || response;
    } catch (error: any) {
      console.error('Failed to validate command:', error);
      throw new Error(error.response?.data?.error || 'Failed to validate command');
    }
  },

  /**
   * Process and execute a command
   * AI automatically handles intent detection, validation, and execution in one call
   * @param params - Command processing parameters
   * @returns Promise with processing result including actions to apply
   */
  async processCommand(params: ProcessCommandParams): Promise<ProcessCommandResponse> {
    try {
      // Single endpoint handles everything with AI
      const response = await post('/report-config/command', params) as any;
      return response.data || response;
    } catch (error: any) {
      console.error('Failed to process command:', error);
      throw new Error(error.response?.data?.error || 'Failed to process command');
    }
  },

  /**
   * Get available commands for a measure
   * @param params - Parameters including measure
   * @returns Promise with available commands
   */
  async getAvailableCommands(params: GetCommandsParams): Promise<GetCommandsResponse> {
    try {
      const response = await get('/report-config/commands', params) as any;
      return response.data || response;
    } catch (error: any) {
      console.error('Failed to load commands:', error);
      // Return default commands if API fails
      return {
        commands: [
          {
            command: 'filter',
            pattern: '*filter <parameters>',
            description: 'Apply filters to narrow down data',
            examples: ['*filter sales > 1000', '*filter status = paid']
          }
        ]
      };
    }
  },

  /**
   * Process multiple commands in batch
   * @param params - Batch processing parameters
   * @returns Promise with batch processing result
   */
  async batchProcessCommands(params: BatchProcessParams): Promise<ProcessCommandResponse> {
    try {
      const response = await post('/report-config/batch', params) as any;
      return response.data || response;
    } catch (error: any) {
      console.error('Failed to process batch commands:', error);
      throw new Error(error.response?.data?.error || 'Failed to process batch commands');
    }
  },

  /**
   * Helper method to parse filter actions
   * @param actions - Array of actions from process response
   * @returns Parsed filter data
   */
  parseFilterActions(actions: any[]): { filters: FilterAction[]; logic: string } {
    const filterAction = actions.find((action: any) => action.type === 'filter');
    if (!filterAction) {
      return { filters: [], logic: 'AND' };
    }

    return {
      filters: filterAction.data.filters || [],
      logic: filterAction.data.logic || 'AND'
    };
  },

  /**
   * Helper method to parse sort action
   * @param actions - Array of actions from process response
   * @returns Parsed sort data
   */
  parseSortAction(actions: any[]): SortAction | null {
    const sortAction = actions.find((action: any) => action.type === 'sort');
    if (!sortAction) {
      return null;
    }

    return {
      column: sortAction.data.column,
      columnName: sortAction.data.columnName || sortAction.data.column,
      direction: sortAction.data.direction || 'desc'
    };
  },

  /**
   * Helper method to parse columns action
   * @param actions - Array of actions from process response
   * @returns Parsed columns data
   */
  parseColumnsAction(actions: any[]): ColumnsAction | null {
    const columnsAction = actions.find((action: any) => action.type === 'columns');
    if (!columnsAction) {
      return null;
    }

    return {
      operation: columnsAction.data.operation || 'add',
      columns: columnsAction.data.columns || []
    };
  },

  /**
   * Load command history from localStorage
   * @returns Array of command history items
   */
  loadCommandHistory(): any[] {
    try {
      const stored = localStorage.getItem('report-assistant-history');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  },

  /**
   * Save command history to localStorage
   * @param history - Array of command history items
   */
  saveCommandHistory(history: any[]): void {
    try {
      // Keep only last 50 items
      const trimmedHistory = history.slice(-50);
      localStorage.setItem('report-assistant-history', JSON.stringify(trimmedHistory));
    } catch (error) {
      console.error('Failed to save command history:', error);
    }
  },

  /**
   * Add a command to history
   * @param command - The command string
   * @param error - Whether the command resulted in an error
   * @returns Updated history array
   */
  addToCommandHistory(command: string, error: boolean = false): any[] {
    const history = this.loadCommandHistory();
    const item = {
      command,
      timestamp: new Date().toISOString(),
      error
    };
    
    history.push(item);
    this.saveCommandHistory(history);
    
    return history;
  },

  /**
   * Clear command history
   */
  clearCommandHistory(): void {
    try {
      localStorage.removeItem('report-assistant-history');
    } catch (error) {
      console.error('Failed to clear command history:', error);
    }
  },

  /**
   * Generate command suggestions based on input
   * @param input - The current input text
   * @returns Array of suggested commands
   */
  generateSuggestions(input: string): string[] {
    const lowerInput = input.toLowerCase();
    const suggestions: string[] = [];

    // Filter-based suggestions
    if (lowerInput.includes('filter') || lowerInput.includes('sales') || lowerInput.includes('revenue')) {
      suggestions.push('*filter sales > 1000');
      suggestions.push('*filter revenue > 5000 and units > 100');
    }

    if (lowerInput.includes('status') || lowerInput.includes('paid')) {
      suggestions.push('*filter status = paid');
      suggestions.push('*filter order_status = "completed"');
    }

    if (lowerInput.includes('product') || lowerInput.includes('stock')) {
      suggestions.push('*filter products with low stock');
      suggestions.push('*filter stock < 10');
    }

    // Sort-based suggestions (for future implementation)
    if (lowerInput.includes('sort') || lowerInput.includes('highest') || lowerInput.includes('lowest')) {
      suggestions.push('*sort revenue desc');
      suggestions.push('*sort units asc');
      suggestions.push('*sort by highest sales');
    }

    // Column-based suggestions (for future implementation)
    if (lowerInput.includes('column') || lowerInput.includes('show') || lowerInput.includes('hide')) {
      suggestions.push('*columns add profit');
      suggestions.push('*columns show only product and sales');
      suggestions.push('*columns remove cost');
    }

    return suggestions.slice(0, 3);
  },

  /**
   * Format operator for display
   * @param operator - The operator code
   * @returns Formatted operator label
   */
  formatOperator(operator: string): string {
    const labels: { [key: string]: string } = {
      'eq': '=',
      'neq': '≠',
      'gt': '>',
      'gte': '≥',
      'lt': '<',
      'lte': '≤',
      'contains': 'contains',
      'not_contains': 'not contains',
      'in': 'in',
      'not_in': 'not in',
      'is_null': 'is empty',
      'is_not_null': 'has value'
    };
    return labels[operator] || operator;
  },

  /**
   * Format value for display
   * @param value - The value to format
   * @returns Formatted value string
   */
  formatValue(value: any): string {
    if (value === null || value === undefined) return 'empty';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toLocaleString();
    return String(value);
  },

  /**
   * Format timestamp for display
   * @param timestamp - The timestamp to format
   * @returns Formatted time string
   */
  formatTimestamp(timestamp: string | Date): string {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    
    return date.toLocaleDateString();
  }
};

export default reportAssistantService;

// Export types for use in components
export type {
  ValidateCommandParams,
  ValidateCommandResponse,
  ProcessCommandParams,
  ProcessCommandResponse,
  GetCommandsParams,
  GetCommandsResponse,
  BatchProcessParams,
  FilterAction,
  SortAction,
  ColumnsAction
};